import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DiscrepancyAlerts = ({ reconciliations, onTriggerAlert, threshold }) => {
  if (!reconciliations || reconciliations?.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="AlertTriangle" size={24} className="text-red-500" />
        Critical Alerts ({reconciliations?.length})
      </h2>
      <div className="space-y-3">
        {reconciliations?.map((reconciliation) => {
          const variance = reconciliation?.actual_balance - reconciliation?.expected_balance;
          const isLargeVariance = Math.abs(variance) > (threshold || 5);
          const hasAiAnalysis = reconciliation?.ai_analysis;
          const alertSent = reconciliation?.alert_sent;

          return (
            <div
              key={reconciliation?.id}
              className={`p-4 rounded-lg border ${
                isLargeVariance 
                  ? 'bg-red-500/10 border-red-500/20' :'bg-orange-500/10 border-orange-500/20'
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon 
                  name={isLargeVariance ? 'AlertTriangle' : 'Clock'} 
                  size={20} 
                  className={isLargeVariance ? 'text-red-500' : 'text-orange-500'} 
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-foreground">
                      {reconciliation?.cash_drawers?.name || 'Drawer'}
                    </h3>
                    <span className={`text-sm font-bold ${
                      isLargeVariance ? 'text-red-500' : 'text-orange-500'
                    }`}>
                      {variance >= 0 ? '+' : ''}${variance?.toFixed(2)} variance
                    </span>
                  </div>

                  {/* Alert Status */}
                  <div className="flex items-center gap-2 mb-2">
                    {alertSent ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-500/10 text-green-500 rounded-full border border-green-500/20">
                        <Icon name="CheckCircle" size={14} />
                        Alert Sent
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-full border border-yellow-500/20">
                        <Icon name="Clock" size={14} />
                        Pending Alert
                      </span>
                    )}
                    {hasAiAnalysis && (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full border border-blue-500/20">
                        <Icon name="Brain" size={14} />
                        AI Analyzed
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {isLargeVariance 
                      ? 'Large variance detected - requires immediate manager attention' 
                      : 'Pending approval required'}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Expected: ${reconciliation?.expected_balance?.toFixed(2)}</span>
                    <span>Actual: ${reconciliation?.actual_balance?.toFixed(2)}</span>
                    <span>Reconciled by: {reconciliation?.user_profiles?.full_name || 'Unknown'}</span>
                  </div>

                  {/* AI Analysis Summary */}
                  {hasAiAnalysis && reconciliation?.ai_analysis?.summary && (
                    <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Icon name="Sparkles" size={16} className="text-blue-500 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-foreground mb-2">{reconciliation?.ai_analysis?.summary}</p>
                          {reconciliation?.ai_analysis?.riskLevel && (
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              reconciliation?.ai_analysis?.riskLevel === 'critical' ? 'bg-red-500/10 text-red-500' :
                              reconciliation?.ai_analysis?.riskLevel === 'high' ? 'bg-orange-500/10 text-orange-500' :
                              reconciliation?.ai_analysis?.riskLevel === 'medium'? 'bg-yellow-500/10 text-yellow-500' : 'bg-green-500/10 text-green-500'
                            }`}>
                              {reconciliation?.ai_analysis?.riskLevel?.toUpperCase()} RISK
                            </span>
                          )}
                        </div>
                      </div>
                      {reconciliation?.ai_analysis?.suspiciousPatterns?.length > 0 && (
                        <div className="mt-2 pl-6">
                          <p className="text-xs font-medium text-red-500 mb-1">⚠️ Suspicious Patterns:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {reconciliation?.ai_analysis?.suspiciousPatterns?.slice(0, 3)?.map((pattern, idx) => (
                              <li key={idx}>• {pattern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {reconciliation?.variance_reason && (
                    <p className="text-sm text-foreground mt-2">
                      <span className="font-medium">Reason:</span> {reconciliation?.variance_reason}
                    </p>
                  )}

                  {/* Manual Alert Trigger */}
                  {!alertSent && onTriggerAlert && (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        onClick={() => onTriggerAlert(reconciliation)}
                        className="flex items-center gap-2"
                      >
                        <Icon name="Send" size={16} />
                        Send Alert Now
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscrepancyAlerts;